import { useMemo } from "react";

/**
 * Returns a stable per-mount random offset in the range `[0, maxMs)`.
 *
 * Many dashboard/layout polling queries share round-number base intervals
 * (60s, 120s, 300s). Over time they drift into lockstep and fire on the same
 * tick — a "thundering herd" of simultaneous DB-backed requests that trips the
 * per-user 429 rate limiter and surfaces "[Area] is busy" toasts during normal
 * browsing. Adding a small, stable random offset per query spreads those
 * refetches out so they no longer all land at once.
 *
 * The offset is computed once per component mount (held for the session) so the
 * cadence stays steady — it doesn't re-randomise on every render.
 */
export function useJitter(maxMs: number): number {
  return useMemo(() => Math.floor(Math.random() * Math.max(0, maxMs)), [maxMs]);
}

/**
 * A jittered refetch interval: the base interval plus a random offset of up to
 * `fraction` (default 20%) of the base. Each call site gets its own independent
 * offset, so queries sharing a base interval de-sync from one another while
 * keeping data freshness effectively unchanged.
 */
export function useJitteredInterval(baseMs: number, fraction = 0.2): number {
  const jitter = useJitter(Math.floor(baseMs * fraction));
  return baseMs + jitter;
}
